/** Upcoming and past bookings. */

import { errorMessage, get } from "@taxi/mobile-web/api";
import { dateParts, money, time } from "@taxi/mobile-web/format";
import { useResource } from "@taxi/mobile-web/hooks";
import { ListIcon } from "@taxi/mobile-web/icons";
import { useRealtime } from "@taxi/mobile-web/realtime";
import { navigate, useLocation } from "@taxi/mobile-web/router";
import { Button, Empty, Loading, Notice, Segmented, TopBar } from "@taxi/mobile-web/ui";
import { useState } from "react";
import { placeTitle } from "../lib/draft";
import type { Booking, Page } from "../lib/types";

export function BookingsScreen() {
  const { query } = useLocation();
  const tab = query.get("tab") === "past" ? "past" : "upcoming";
  const [more, setMore] = useState<Booking[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const list = useResource(async () => {
    const page = await get<Page<Booking>>(`/customer/bookings?when=${tab}&limit=20`);
    setMore([]);
    setCursor(page.nextCursor);
    return page.data;
  }, [tab]);
  useRealtime((event) => {
    if (
      event.type === "job.updated" ||
      event.type === "job.created" ||
      event.type === "reconnected"
    )
      void list.reload();
  }, tab === "upcoming");

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const page = await get<Page<Booking>>(
        `/customer/bookings?when=${tab}&limit=20&cursor=${encodeURIComponent(cursor)}`,
      );
      setMore((current) => [...current, ...page.data]);
      setCursor(page.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }

  const bookings = [...(list.data ?? []), ...more];
  return (
    <div className="bookings">
      <TopBar title="Bookings" />
      <div className="screen-body">
        <Segmented
          label="Which bookings"
          value={tab}
          options={[
            { value: "upcoming", label: "Upcoming" },
            { value: "past", label: "Past" },
          ]}
          onChange={(value) =>
            navigate(value === "past" ? "/bookings?tab=past" : "/bookings", { replace: true })
          }
        />
        {list.error ? <Notice tone="error">{errorMessage(list.error)}</Notice> : null}
        {list.loading && !list.data ? (
          <Loading />
        ) : bookings.length === 0 ? (
          <Empty
            icon={<ListIcon size={32} />}
            title={tab === "upcoming" ? "Nothing booked" : "No trips yet"}
          >
            <p>
              {tab === "upcoming"
                ? "Your next trip will show here."
                : "Your past trips and receipts will show here."}
            </p>
            {tab === "upcoming" && (
              <Button block={false} onClick={() => navigate("/book")}>
                Book a taxi
              </Button>
            )}
          </Empty>
        ) : (
          <ul className="booking-list">
            {bookings.map((b) => {
              const at = new Date(b.pickupAt);
              const day = dateParts(at);
              const now = b.isAsap && tab === "upcoming";
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    className={`booking-item booking-item-${b.status}`}
                    onClick={() => navigate(`/bookings/${b.id}`)}
                  >
                    <span className="booking-item-date" aria-hidden="true">
                      {now ? (
                        <strong>Now</strong>
                      ) : (
                        <>
                          <span>{day.weekday}</span>
                          <strong>{day.day}</strong>
                          <span>{day.month}</span>
                        </>
                      )}
                    </span>
                    <span className="booking-item-body">
                      <span className="booking-item-top">
                        <span className="booking-item-time">
                          {now ? "As soon as possible" : time(at)} · {b.vehicleType.name}
                        </span>
                      </span>
                      <span className="booking-item-place">
                        <i className="route-dot route-dot-pickup" />
                        {placeTitle(b.pickup)}
                      </span>
                      <span className="booking-item-place">
                        <i className="route-dot route-dot-dropoff" />
                        {placeTitle(b.dropoff)}
                      </span>
                    </span>
                    <span className="booking-item-end">
                      <span className="booking-item-price">
                        {money(b.finalFarePence ?? b.pricePence)}
                        {b.isEstimate ? <small> est.</small> : null}
                      </span>
                      <span className={`status-pill status-${b.status}`}>{b.statusLabel}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {cursor && (
          <Button variant="secondary" loading={loadingMore} onClick={loadMore}>
            Show more
          </Button>
        )}
      </div>
    </div>
  );
}
